import { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/AdminService';

/**
 * Deep links from Slack ("Open order") carry the order's organization as
 * `?org=<id>`. A global admin who opens one is switched into that
 * organization first (the same switch as WorkingOrgSwitcher), so the order
 * shows as its members see it; OtherOrgWarning then frames the page.
 *
 * Everyone else — and an admin already in that organization, or
 * impersonating — just has the parameter dropped from the URL. The parameter
 * is removed before any reload, so a failed switch can never loop.
 */
export const orgFromSearch = (search: string): number | null => {
  const raw = new URLSearchParams(search).get('org');
  return raw && /^\d+$/.test(raw) ? Number(raw) : null;
};

export const urlWithoutOrg = (href: string): string => {
  const url = new URL(href);
  url.searchParams.delete('org');
  return url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') + url.hash;
};

const OrgFromLink: React.FC = () => {
  const { currentUser, isImpersonating } = useAuth();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current || !currentUser) return;
    if (!new URLSearchParams(window.location.search).has('org')) return;
    handled.current = true;
    const target = orgFromSearch(window.location.search);
    const clean = urlWithoutOrg(window.location.href);
    window.history.replaceState(window.history.state, '', clean);
    const isAdmin = currentUser.role === 'admin';
    if (!isAdmin || isImpersonating || target == null || target === currentUser.organization_id) return;
    adminService
      .setWorkingOrganization(target)
      // Orders, catalogs, settings and cart are org-scoped and cached in
      // several contexts — a full reload is the reliable way to switch.
      .then(() => window.location.replace(clean))
      .catch(err => console.error('Could not switch to the order\'s organization:', err));
  }, [currentUser, isImpersonating]);

  return null;
};

export default OrgFromLink;
